import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, defer, from, map, mergeMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiListResponse } from '../core/response-models/api-list-response.model';
import { ApiResponse } from '../core/response-models/api-response.model';
import { ApiError } from './api-error';
import { isRecord } from '../core/utils/is-record';
import { PathSegments } from './path-segments';
import { QueryParams } from './query-params';
import { RequestOptions } from './request-options';

/**
 * Keys the Hub owns, stripped from every create/update body. This is only a backstop: every service must
 * still build an explicit allowlisted payload (as `toDroneRequest` does). `organizationId` is deliberately
 * NOT listed, because several Hub controllers read it from the body.
 */
const SERVER_OWNED_KEYS = ['id', 'createdAt', 'updatedAt', 'deletedAt', 'createdBy', 'updatedBy'];
/** Characters that could change how a path segment is parsed, or smuggle pre-encoded text. */
const UNSAFE_SEGMENT_CHARS = /[?#\\%]/;
/** `scheme:` at the start of a value, e.g. `https:` or `javascript:`. */
const URL_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** HttpClient options: only query params, because headers are the auth interceptor's job. */
export type HttpRequestOptions = { params: Record<string, string | number | boolean> };

/**
 * Base for every service that calls the Hub. Owns the plumbing: URL building and validation, query params,
 * lazy sending, unwrapping the Hub's `ResponseModel` envelope, the server-owned-key strip for bodies, and
 * mapping every failure to an `ApiError`.
 *
 * A Hub resource with an entity model uses `BaseService<T>` (which extends this). A Hub endpoint with no
 * entity model (payments, tokens, stats) extends this directly with `@Injectable({ providedIn: 'root' })`
 * and `super('<endpoint>')`, and calls `request<R>()`. Either way a service only reaches URLs under its
 * own endpoint. Auth and `X-Organization-Id` headers belong to the auth interceptor; target another org
 * with the `organizations/{id}` path prefix instead.
 *
 * Only `inject` and `HttpClient` come from Angular, so specs can mock both (this repo's Jest cannot load
 * Angular's ESM bundles).
 */
export abstract class ApiService {
  protected readonly http = inject(HttpClient);

  constructor(protected readonly endpoint: string) {}

  /**
   * Like `request<R>()`, but `read` receives the whole response body instead of just `data`, for list
   * endpoints that return fields beside `data` and `paging` (e.g. chemical search's counts). Same URL
   * validation, body handling and `ApiError` mapping.
   */
  protected requestEnvelope<R>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    opts: RequestOptions,
    read: (body: ApiListResponse<unknown> | null) => R,
    body?: unknown
  ): Observable<R> {
    return this.send(
      () => this.http.request<ApiListResponse<unknown>>(method, this.buildUrl(opts), this.withBody(method, opts, body)),
      response => read(response ?? null)
    );
  }

  /**
   * A request to `/{prefix}/{endpoint}/{id}/{path}` (`opts.id` is one validated segment), with URL validation, params and ApiError mapping.
   * POST/PUT/PATCH bodies (and a DELETE body, when given) are stripped of server-owned keys. `read` receives the
   * unwrapped `data` (which may be `null`).
   */
  protected request<R>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    opts: RequestOptions,
    read: (data: unknown) => R,
    body?: unknown
  ): Observable<R> {
    return this.send(
      () => this.http.request<ApiResponse<unknown>>(method, this.buildUrl(opts), this.withBody(method, opts, body)),
      response => read(response?.data)
    );
  }

  /**
   * The one URL builder: `environment.apiUrl/{prefix}/{endpoint}/{id}/{path}`, where the id is the `id`
   * argument (CRUD) or `opts.id` (never both). The endpoint is always
   * included, so a service can only reach URLs under its own resource. Every segment is validated
   * and encoded, so the result can never leave `environment.apiUrl`. Throws an `INVALID_REQUEST` ApiError
   * on an unsafe segment (callers run it inside `send`, so it reaches subscribers).
   */
  protected buildUrl(opts: RequestOptions, id?: string): string {
    if (id !== undefined && opts.id !== undefined) {
      throw invalidRequest();
    }
    const segments = [
      ...toSegments(opts.prefix),
      ...toSegments(this.endpoint),
      ...toIdSegment(id ?? opts.id),
      ...toSegments(opts.path),
    ];
    const base = environment.apiUrl.replace(/\/+$/, '');
    return [base, ...segments.map(segment => encodeURIComponent(segment))].join('/');
  }

  /** HttpClient options for `opts`: only query params, with `null` / `undefined` values dropped. */
  protected requestOptions(opts: RequestOptions): HttpRequestOptions {
    return { params: toParams(opts.query) };
  }

  /**
   * A shallow copy of `body` without server-owned keys. Never mutates the caller's object. File uploads
   * (`FormData`, `Blob`) pass through untouched, because spreading them would empty them.
   */
  protected stripServerOwnedKeys(body: unknown): unknown {
    if (!isRecord(body) || isUploadBody(body)) {
      return body;
    }
    const copy: Record<string, unknown> = { ...body };
    for (const key of SERVER_OWNED_KEYS) {
      delete copy[key];
    }
    return copy;
  }

  /**
   * GETs a file under the endpoint as a `Blob` (report exports, attachments), with the same URL validation.
   * A failed download's body is a Blob too, so its JSON envelope is read back out before it is mapped to
   * `ApiError`, keeping the hub's message and code. Never link to a hub file route with a plain `href`: it
   * needs the auth interceptor.
   */
  protected download(opts: RequestOptions): Observable<Blob> {
    return defer(() => this.http.get(this.buildUrl(opts), { ...this.requestOptions(opts), responseType: 'blob' as const })).pipe(
      catchError(err => from(readBlobErrorBody(err)).pipe(mergeMap(rethrowAsApiError)))
    );
  }

  /**
   * Builds and sends the request lazily (on subscribe), so a URL validation failure reaches the
   * subscriber as an `ApiError` like any other failure, instead of throwing at call time.
   */
  protected send<R, O>(request: () => Observable<R>, project: (response: R) => O): Observable<O> {
    return defer(request).pipe(map(project), catchError(rethrowAsApiError));
  }

  /** Adds the stripped body for POST/PUT/PATCH, and for DELETE only when one is given; GET sends none. */
  private withBody(method: string, opts: RequestOptions, body: unknown): HttpRequestOptions & { body?: unknown } {
    const options = this.requestOptions(opts);
    const sendsBody = method === 'POST' || method === 'PUT' || method === 'PATCH' || (method === 'DELETE' && body !== undefined);
    return sendsBody ? { ...options, body: this.stripServerOwnedKeys(body) } : options;
  }
}

function rethrowAsApiError(err: unknown): Observable<never> {
  return throwError(() => ApiError.fromHttpError(err));
}

/** For a failed blob request, parses the Blob body back into the JSON envelope it carries (if any). */
async function readBlobErrorBody(err: unknown): Promise<unknown> {
  if (!isRecord(err) || typeof err['status'] !== 'number' || !(typeof Blob !== 'undefined' && err['error'] instanceof Blob)) {
    return err;
  }
  try {
    return { status: err['status'], error: JSON.parse(await err['error'].text()) };
  } catch {
    return { status: err['status'], error: null };
  }
}

/** A rejected URL. Generic on purpose: it never carries the URL or the offending segment. */
function invalidRequest(): ApiError {
  return new ApiError(0, [], { code: 'INVALID_REQUEST' }, 'Invalid request');
}

/** True for file-upload bodies, which must be sent as they are. */
function isUploadBody(body: unknown): boolean {
  return (typeof FormData !== 'undefined' && body instanceof FormData) || (typeof Blob !== 'undefined' && body instanceof Blob);
}

/**
 * Validated segments for a prefix/path. A string is split on `/` (leading and trailing slashes ignored);
 * in an array each element must be exactly one segment, so an id inside it can never add segments.
 */
function toSegments(value: PathSegments | undefined): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(segment => {
      // Unlike an omitted opts.id, an undefined element would silently change the route, so it is rejected.
      if (typeof segment !== 'string') {
        throw invalidRequest();
      }
      return toIdSegment(segment);
    });
  }
  const trimmed = ((value as string | undefined) ?? '').trim();
  if (trimmed.startsWith('//') || URL_SCHEME.test(trimmed)) {
    throw invalidRequest();
  }
  const inner = trimmed.replace(/^\/+|\/+$/g, '');
  return inner === '' ? [] : inner.split('/').map(validateSegment);
}

/** An id must be exactly one safe segment. */
function toIdSegment(id: string | undefined): string[] {
  if (id === undefined) {
    return [];
  }
  if (typeof id !== 'string' || id.includes('/')) {
    throw invalidRequest();
  }
  return [validateSegment(id)];
}

function validateSegment(segment: string): string {
  if (segment === '' || segment === '.' || segment === '..' || UNSAFE_SEGMENT_CHARS.test(segment)) {
    throw invalidRequest();
  }
  return segment;
}

/** Drops `null` / `undefined` values; HttpClient encodes the rest. */
function toParams(query: QueryParams | undefined): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined) {
      params[key] = value;
    }
  }
  return params;
}
